import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Chrome, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/data/content";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onClose, resetToken = null, onResetDone }) {
  const { setUser } = useAuth();
  const [tab, setTab] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const isReset = !!resetToken;

  useEffect(() => {
    if (open) {
      setError("");
      setNotice("");
    }
  }, [open, resetToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (isReset) {
        if (form.password !== form.confirm) throw new Error("Passwords do not match");
        await axios.post(`${API_URL}/auth/reset-password`, { token: resetToken, new_password: form.password }, { withCredentials: true });
        onResetDone?.();
        setForm({ name: "", email: form.email, password: "", confirm: "" });
        setTab("login");
        setNotice("Password updated — sign in with your new password.");
        toast.success("Password updated. See you at Chandbagh!");
      } else if (tab === "forgot") {
        await axios.post(`${API_URL}/auth/forgot-password`, { email: form.email }, { withCredentials: true });
        setNotice("If an account exists for that email, a reset link is on its way — check your inbox (and spam).");
      } else {
        const url = tab === "login" ? "/auth/login" : "/auth/register";
        const { data } = await axios.post(`${API_URL}${url}`, { name: form.name, email: form.email, password: form.password }, { withCredentials: true });
        setUser(data);
        toast.success(tab === "login" ? `Welcome back, ${data.name.split(" ")[0]}!` : `Your Dosco account is ready, ${data.name.split(" ")[0]}!`);
        onClose();
      }
    } catch (err) {
      const msg = err?.response ? formatApiErrorDetail(err.response?.data?.detail) : err?.message;
      setError(msg || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const title = isReset ? "Choose a New Password" : tab === "forgot" ? "Reset Your Password" : tab === "login" ? "Welcome Back" : "Join the Doon Family";

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="auth-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="auth-modal-backdrop" />
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="relative z-10 w-full max-w-md rounded-3xl border border-line/25 bg-ink p-8"
          >
            <button onClick={onClose} aria-label="Close" data-testid="auth-modal-close" className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full text-soft transition-colors hover:text-gold">
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15 font-display font-bold text-gold">91</div>
              <div>
                <h3 className="font-display text-xl font-bold text-fg">{title}</h3>
                <p className="text-xs text-soft">{isReset ? "Reset link accepted • valid for 60 minutes" : "91st Founder's Day • Chandbagh, Dehradun"}</p>
              </div>
            </div>

            {!isReset && tab !== "forgot" && (
              <div className="mt-6 grid grid-cols-2 gap-1 rounded-full border border-line/20 p-1" data-testid="auth-tabs">
                {[["login", "Sign In"], ["register", "Create Account"]].map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => { setTab(id); setError(""); setNotice(""); }}
                    data-testid={`auth-tab-${id}`}
                    className={`rounded-full py-2 text-xs font-bold tracking-widest transition-all duration-300 ${tab === id ? "bg-gold text-navy" : "text-soft hover:text-fg"}`}
                  >
                    {label.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              {isReset ? (
                <>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="New password (min 8 characters)"
                    data-testid="auth-new-password-input"
                    className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                  />
                  <input
                    type="password"
                    required
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    placeholder="Confirm new password"
                    data-testid="auth-confirm-password-input"
                    className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                  />
                </>
              ) : tab === "forgot" ? (
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="The email on your account"
                  data-testid="auth-forgot-email-input"
                  className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                />
              ) : (
                <>
                  {tab === "register" && (
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      data-testid="auth-name-input"
                      maxLength={60}
                      className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                    />
                  )}
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address"
                    data-testid="auth-email-input"
                    className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                  />
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={tab === "login" ? "Password" : "Password (min 8 characters)"}
                    data-testid="auth-password-input"
                    className="w-full rounded-xl border border-line/20 bg-transparent px-4 py-3 text-sm text-fg outline-none placeholder:text-soft/60 focus:border-gold/60"
                  />
                </>
              )}

              {error && <p className="rounded-xl border border-red-400/40 bg-red-400/10 px-4 py-3 text-xs text-red-300" data-testid="auth-error">{error}</p>}
              {notice && <p className="rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-xs text-gold" data-testid="auth-notice">{notice}</p>}

              <button
                type="submit"
                disabled={busy}
                data-testid="auth-submit-button"
                className="ripple-host w-full rounded-full bg-gold py-3.5 font-display text-sm font-bold tracking-[0.2em] text-navy transition-all duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {busy ? "ONE MOMENT…" : isReset ? "SAVE NEW PASSWORD" : tab === "forgot" ? "EMAIL ME A RESET LINK" : tab === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
              </button>
            </form>

            {isReset ? null : tab === "forgot" ? (
              <button onClick={() => { setTab("login"); setNotice(""); }} data-testid="auth-back-to-login" className="mt-5 text-xs text-soft transition-colors hover:text-gold">
                ← Back to sign in
              </button>
            ) : (
              <>
                {tab === "login" && (
                  <button onClick={() => { setTab("forgot"); setError(""); setNotice(""); }} data-testid="auth-forgot-link" className="mt-3 text-xs text-soft transition-colors hover:text-gold">
                    Forgot password?
                  </button>
                )}
                <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-soft/60">
                  <span className="h-px flex-1 bg-line/15" /> or <span className="h-px flex-1 bg-line/15" />
                </div>
                <button
                  onClick={googleSignIn}
                  data-testid="auth-google-button"
                  className="glass flex w-full items-center justify-center gap-3 rounded-full py-3.5 text-sm font-semibold text-fg transition-all duration-300 hover:border-gold/40 hover:text-gold"
                >
                  <Chrome className="h-4 w-4" /> Continue with Google
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
